import api from './api';
import type {
  CostLineRow,
  CostLinesSaveResponse,
  DefectSaveRow,
  DefectsSaveResponse,
  DriverSummary,
  Expirations,
  IncidentDetail,
  IncidentSaveBody,
  IncidentSaveResponse,
  IncidentsListParams,
  IncidentsListResponse,
  MaintenanceDetail,
  MaintenanceListParams,
  MaintenanceListResponse,
  MaintenanceSaveBody,
  MaintenanceSaveResponse,
  RecordsBuckets,
  SafetyDocument,
  SafetyFreeLiabilityAdd,
  SafetyFreeLiabilityAddResponse,
  SafetyFreeLiabilityRow,
  SafetyHistoryResponse,
  SafetyLookupOption,
  SafetyNote,
  SafetyUnitNotification,
  SafetyTableCatalogResponse,
  SafetyTableRecord,
  SafetyTableRows,
  SafetyTableSaveBody,
  SafetyTableSaveResponse,
  SafetyRegion,
  TicketDetail,
  TicketLinkResponse,
  TicketLookups,
  TicketSaveBody,
  TicketSaveResponse,
  TicketsListParams,
  TicketsListResponse,
  UnitChildren,
  UnitChildrenSaveBody,
  UnitChildrenSaveResponse,
  UnitDetail,
  UnitDriverFieldsSaveResponse,
  UnitPayroll,
  UnitPayrollReveal,
  UnitPayrollSaveResponse,
  UnitsListParams,
  UnitsListResponse,
  ViolationLookupRow,
} from '../types/safetySystem';

export interface SafetySystemStatus {
  enabled: boolean;
}

const safetySystemService = {
  /** GET /safety-system/status — reflects the backend SAFETY_SYSTEM_ENABLED env flag. */
  async status(): Promise<SafetySystemStatus> {
    const r = await api.get('/safety-system/status');
    return r.data;
  },

  // ---- Hub dashboard -------------------------------------------------------

  async driverSummary(region = 'ALL', signal?: AbortSignal): Promise<DriverSummary> {
    return (await api.get('/safety-system/dashboard/driver-summary', { params: { region }, signal })).data as DriverSummary;
  },

  async records(signal?: AbortSignal): Promise<RecordsBuckets> {
    return (await api.get('/safety-system/dashboard/records', { signal })).data as RecordsBuckets;
  },

  async expirations(signal?: AbortSignal): Promise<Expirations> {
    return (await api.get('/safety-system/dashboard/expirations', { signal })).data as Expirations;
  },

  /**
   * Safety regions (`{SAFETY_SCHEMA}.region WHERE deleted='N'`), reusing the existing
   * drivers.py endpoint `GET /safety/regions` which returns `{ data: [{ id, name }] }`.
   */
  async regions(signal?: AbortSignal): Promise<SafetyRegion[]> {
    const r = await api.get('/safety/regions', { signal });
    const rows: { id: number; name: string }[] = r.data?.data ?? [];
    return rows.map((row) => ({ recno: row.id, label: row.name }));
  },

  // ---- Units grid ----------------------------------------------------------

  async listUnits(params: UnitsListParams, signal?: AbortSignal): Promise<UnitsListResponse> {
    return (await api.get('/safety-system/units', { params, signal })).data as UnitsListResponse;
  },

  // ---- Unit drawer ---------------------------------------------------------

  async getUnit(unitrecno: number, signal?: AbortSignal): Promise<UnitDetail> {
    return (await api.get(`/safety-system/units/${unitrecno}`, { signal })).data as UnitDetail;
  },

  async saveDriverTab(unitrecno: number, body: { driver: Record<string, any>; unit: Record<string, any> }) {
    return (await api.put(`/safety-system/units/${unitrecno}/driver`, body)).data;
  },

  async saveTruckTab(
    unitrecno: number,
    body: { truck: Record<string, any>; owner: Record<string, any>; ownerrecno?: number },
  ) {
    return (await api.put(`/safety-system/units/${unitrecno}/truck`, body)).data;
  },

  // ---- Unit drawer: Cert / Payroll / Retention tabs + Truck grids (Phase 3) ----

  /** GET /safety-system/units/{recno}/children — the nine child lists, the Cert/Retention driver scalars and the pick lists. */
  async getUnitChildren(unitrecno: number, signal?: AbortSignal): Promise<UnitChildren> {
    return (await api.get(`/safety-system/units/${unitrecno}/children`, { signal })).data as UnitChildren;
  },

  /** PUT /safety-system/units/{recno}/children — only the touched lists; 422 `{message, errors:[{key,index,field,message}]}`. */
  async saveUnitChildren(unitrecno: number, body: UnitChildrenSaveBody): Promise<UnitChildrenSaveResponse> {
    return (await api.put(`/safety-system/units/${unitrecno}/children`, body)).data as UnitChildrenSaveResponse;
  },

  /** PUT /safety-system/units/{recno}/cert `{driver: {changed cert columns}}`. */
  async saveUnitCert(unitrecno: number, body: { driver: Record<string, any> }): Promise<UnitDriverFieldsSaveResponse> {
    return (await api.put(`/safety-system/units/${unitrecno}/cert`, body)).data as UnitDriverFieldsSaveResponse;
  },

  /** PUT /safety-system/units/{recno}/retention `{driver: {changed retention columns}}`. */
  async saveUnitRetention(unitrecno: number, body: { driver: Record<string, any> }): Promise<UnitDriverFieldsSaveResponse> {
    return (await api.put(`/safety-system/units/${unitrecno}/retention`, body)).data as UnitDriverFieldsSaveResponse;
  },

  /** GET /safety-system/units/{recno}/payroll — flags, current insurance, settlements, MASKED direct deposit. */
  async getUnitPayroll(unitrecno: number, signal?: AbortSignal): Promise<UnitPayroll> {
    return (await api.get(`/safety-system/units/${unitrecno}/payroll`, { signal })).data as UnitPayroll;
  },

  /**
   * PUT /safety-system/units/{recno}/payroll `{unit: {...}}` — payroll flags / DD text fields plus
   * any `dd_routing1/2`, `dd_account1/2` the user typed (digits only; '' clears). 422 structured, 409 string.
   */
  async saveUnitPayroll(unitrecno: number, body: { unit: Record<string, any> }): Promise<UnitPayrollSaveResponse> {
    return (await api.put(`/safety-system/units/${unitrecno}/payroll`, body)).data as UnitPayrollSaveResponse;
  },

  /** POST /safety-system/units/{recno}/payroll/reveal — audited plaintext bank numbers (sensitive gate). */
  async revealUnitPayroll(unitrecno: number): Promise<UnitPayrollReveal> {
    return (await api.post(`/safety-system/units/${unitrecno}/payroll/reveal`)).data as UnitPayrollReveal;
  },

  /** GET /safety-system/documents — newest first; each item carries an absolute blob `url`. */
  async listDocuments(tablename: string, tablerecno: number): Promise<{ data: SafetyDocument[]; count: number }> {
    return (await api.get('/safety-system/documents', { params: { tablename, tablerecno } })).data;
  },

  /** POST /safety-system/documents (multipart). `documenttype` is free text (e.g. PHOTO). */
  async uploadDocument(
    tablename: string, tablerecno: number, file: File, documenttype: string, description = '',
  ): Promise<{ success: boolean; document: SafetyDocument }> {
    const form = new FormData();
    form.append('tablename', tablename);
    form.append('tablerecno', String(tablerecno));
    form.append('documenttype', documenttype);
    form.append('description', description);
    form.append('file', file);
    return (await api.post('/safety-system/documents', form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },

  /** GET /safety-system/documents/{recno} streams the file. */
  async downloadDocument(recno: number): Promise<Blob> {
    return (await api.get(`/safety-system/documents/${recno}`, { responseType: 'blob' })).data as Blob;
  },

  /** DELETE /safety-system/documents/{recno} — soft delete. */
  async deleteDocument(recno: number): Promise<{ success: boolean }> {
    return (await api.delete(`/safety-system/documents/${recno}`)).data;
  },

  /**
   * GET /safety-system/units/{unitrecno}/history — V1's unit History tab: `unit_history`
   * (by unit) + `driver_history` (by driver) + `truck_history` (by truck) merged, newest
   * first, each row tagged with its `source` table.
   */
  async getUnitHistory(unitrecno: number, signal?: AbortSignal): Promise<SafetyHistoryResponse> {
    return (await api.get(`/safety-system/units/${unitrecno}/history`, { signal })).data as SafetyHistoryResponse;
  },

  async listNotes(tablename: string, tablerecno: number): Promise<{ data: SafetyNote[]; count: number }> {
    return (await api.get('/safety-system/notes', { params: { tablename, tablerecno } })).data;
  },

  async addNote(tablename: string, tablerecno: number, notes: string): Promise<{ success: boolean; recno: number }> {
    return (await api.post('/safety-system/notes', { tablename, tablerecno, notes })).data;
  },

  // ---- Tickets (Phase 2, tickets.py) ----------------------------------------

  /** GET /safety-system/tickets?view=ticket|tv&open=&q=&search_by=&include_deleted= */
  async listTickets(params: TicketsListParams, signal?: AbortSignal): Promise<TicketsListResponse> {
    return (await api.get('/safety-system/tickets', { params, signal })).data as TicketsListResponse;
  },

  async getTicket(recno: number, signal?: AbortSignal): Promise<TicketDetail> {
    return (await api.get(`/safety-system/tickets/${recno}`, { signal })).data as TicketDetail;
  },

  /** POST /safety-system/tickets — 201; 409 `{message, dupes}` unless `bypass_dupe`; 422 string. */
  async createTicket(body: TicketSaveBody): Promise<TicketSaveResponse> {
    return (await api.post('/safety-system/tickets', body)).data as TicketSaveResponse;
  },

  /** PUT /safety-system/tickets/{recno} — same body / errors as create; `ticket.closed` closes or reopens. */
  async saveTicket(recno: number, body: TicketSaveBody): Promise<TicketSaveResponse> {
    return (await api.put(`/safety-system/tickets/${recno}`, body)).data as TicketSaveResponse;
  },

  /** GET /safety-system/tickets/lookups — officers + equipment owners for a ticket drawer with no recno yet. */
  async ticketLookups(signal?: AbortSignal): Promise<TicketLookups> {
    return (await api.get('/safety-system/tickets/lookups', { signal })).data as TicketLookups;
  },

  /** GET /safety-system/tickets/lookups/violations?q= — prefix typeahead over the violation table. */
  async violationLookup(q: string, signal?: AbortSignal): Promise<{ data: ViolationLookupRow[]; count: number }> {
    return (await api.get('/safety-system/tickets/lookups/violations', { params: { q }, signal })).data;
  },

  async getTicketHistory(recno: number, signal?: AbortSignal): Promise<SafetyHistoryResponse> {
    return (await api.get(`/safety-system/tickets/${recno}/history`, { signal })).data as SafetyHistoryResponse;
  },

  // ---- Maintenance reports (Phase 2, maintenance.py) -------------------------

  /** GET /safety-system/maintenance-reports?open=&status=&unitnumber=&year=&month=&include_deleted= */
  async listMaintenance(params: MaintenanceListParams, signal?: AbortSignal): Promise<MaintenanceListResponse> {
    return (await api.get('/safety-system/maintenance-reports', { params, signal })).data as MaintenanceListResponse;
  },

  async getMaintenance(recno: number, signal?: AbortSignal): Promise<MaintenanceDetail> {
    return (await api.get(`/safety-system/maintenance-reports/${recno}`, { signal })).data as MaintenanceDetail;
  },

  async createMaintenance(body: MaintenanceSaveBody): Promise<MaintenanceSaveResponse> {
    return (await api.post('/safety-system/maintenance-reports', body)).data as MaintenanceSaveResponse;
  },

  async saveMaintenance(recno: number, body: MaintenanceSaveBody): Promise<MaintenanceSaveResponse> {
    return (await api.put(`/safety-system/maintenance-reports/${recno}`, body)).data as MaintenanceSaveResponse;
  },

  /** PUT /safety-system/maintenance-reports/{recno}/defects — checklist upsert keyed by `defectrecno`. */
  async saveDefects(recno: number, defects: DefectSaveRow[]): Promise<DefectsSaveResponse> {
    return (await api.put(`/safety-system/maintenance-reports/${recno}/defects`, { defects })).data as DefectsSaveResponse;
  },

  async getMaintenanceHistory(recno: number, signal?: AbortSignal): Promise<SafetyHistoryResponse> {
    return (await api.get(`/safety-system/maintenance-reports/${recno}/history`, { signal })).data as SafetyHistoryResponse;
  },

  // ---- Incidents (Phase 2, incidents.py / incidents_save.py) -----------------

  /** GET /safety-system/incidents?view=incident|invparty|equipcargo|claim|cost&open=&q=&search_by=&include_deleted= */
  async listIncidents(params: IncidentsListParams, signal?: AbortSignal): Promise<IncidentsListResponse> {
    return (await api.get('/safety-system/incidents', { params, signal })).data as IncidentsListResponse;
  },

  async getIncident(recno: number, signal?: AbortSignal): Promise<IncidentDetail> {
    return (await api.get(`/safety-system/incidents/${recno}`, { signal })).data as IncidentDetail;
  },

  async createIncident(body: IncidentSaveBody): Promise<IncidentSaveResponse> {
    return (await api.post('/safety-system/incidents', body)).data as IncidentSaveResponse;
  },

  async saveIncident(recno: number, body: IncidentSaveBody): Promise<IncidentSaveResponse> {
    return (await api.put(`/safety-system/incidents/${recno}`, body)).data as IncidentSaveResponse;
  },

  /** PUT /safety-system/incidents/{recno}/costs/{acrecno}/lines — claim cost lines of one associated cost. */
  async saveCostLines(recno: number, acrecno: number, lines: CostLineRow[]): Promise<CostLinesSaveResponse> {
    return (await api.put(`/safety-system/incidents/${recno}/costs/${acrecno}/lines`, { lines })).data as CostLinesSaveResponse;
  },

  /** POST /safety-system/incidents/{recno}/tickets `{ticketnum}` — 404 unknown ticket, 409 linked elsewhere. */
  async linkTicket(recno: number, ticketnum: string): Promise<TicketLinkResponse> {
    return (await api.post(`/safety-system/incidents/${recno}/tickets`, { ticketnum })).data as TicketLinkResponse;
  },

  async unlinkTicket(recno: number, ticketrecno: number): Promise<TicketLinkResponse> {
    return (await api.delete(`/safety-system/incidents/${recno}/tickets/${ticketrecno}`)).data as TicketLinkResponse;
  },

  async getIncidentHistory(recno: number, signal?: AbortSignal): Promise<SafetyHistoryResponse> {
    return (await api.get(`/safety-system/incidents/${recno}/history`, { signal })).data as SafetyHistoryResponse;
  },

  // ---- Safety Tables (Phase 5, tablemaint/) ---------------------------------

  /** GET /safety-system/tables — the specs the caller may see (level >= 1) plus the link-out entries. */
  async tablesCatalog(signal?: AbortSignal): Promise<SafetyTableCatalogResponse> {
    return (await api.get('/safety-system/tables', { signal })).data as SafetyTableCatalogResponse;
  },

  /** GET /safety-system/tables/{key}?include_deleted=&options_only= — grid rows + lookup options; `optionsOnly` skips the row query (`rows: []`). */
  async tableRows(
    key: string, { includeDeleted = false, optionsOnly = false }: { includeDeleted?: boolean; optionsOnly?: boolean } = {}, signal?: AbortSignal,
  ): Promise<SafetyTableRows> {
    const params: Record<string, boolean> = { include_deleted: includeDeleted };
    if (optionsOnly) params.options_only = true;
    return (await api.get(`/safety-system/tables/${key}`, { params, signal })).data as SafetyTableRows;
  },

  /** GET /safety-system/tables/{key}/{recno}?include_deleted= — one row + children (+ soft-deleted ones with `includeDeleted`) + options. */
  async tableRow(key: string, recno: number, { includeDeleted = false }: { includeDeleted?: boolean } = {}, signal?: AbortSignal): Promise<SafetyTableRecord> {
    return (await api.get(`/safety-system/tables/${key}/${recno}`, { params: { include_deleted: includeDeleted }, signal })).data as SafetyTableRecord;
  },

  /** POST /safety-system/tables/{key} — 201 `{recno}`; 405 readonly / no_create, 409 duplicate, 422 validation. */
  async createTableRow(key: string, body: SafetyTableSaveBody): Promise<SafetyTableSaveResponse> {
    return (await api.post(`/safety-system/tables/${key}`, body)).data as SafetyTableSaveResponse;
  },

  /** PUT /safety-system/tables/{key}/{recno} — `{ok, changed, children}`; body = changed editable fields + full child arrays. */
  async saveTableRow(key: string, recno: number, body: SafetyTableSaveBody): Promise<SafetyTableSaveResponse> {
    return (await api.put(`/safety-system/tables/${key}/${recno}`, body)).data as SafetyTableSaveResponse;
  },

  /** GET /safety-system/tables/{key}/{recno}/history — `tablemaint_history` rows, newest first. */
  async tableRowHistory(key: string, recno: number, signal?: AbortSignal): Promise<SafetyHistoryResponse> {
    return (await api.get(`/safety-system/tables/${key}/${recno}/history`, { signal })).data as SafetyHistoryResponse;
  },

  /** POST /safety-system/tables/owner/{recno}/reveal — audited plaintext Tax ID / SSN (payroll-sensitive level 2). */
  async revealOwnerSsn(recno: number): Promise<{ value: string }> {
    return (await api.post(`/safety-system/tables/owner/${recno}/reveal`)).data as { value: string };
  },

  /** GET /safety-system/tables/lookups/drivers?q= — prefix typeahead `[{value: driver recno, label}]`. */
  async driverLookup(q: string, signal?: AbortSignal): Promise<SafetyLookupOption[]> {
    return (await api.get('/safety-system/tables/lookups/drivers', { params: { q }, signal })).data as SafetyLookupOption[];
  },

  // ---- Deferred items (Phase 5, free_liability.py / unit_notifications.py) ----

  /** GET /safety-system/units/{unitrecno}/free-liability — the unit's FREE LIABILITY reimburse rows, newest first. */
  async freeLiability(unitrecno: number, signal?: AbortSignal): Promise<SafetyFreeLiabilityRow[]> {
    return (await api.get(`/safety-system/units/${unitrecno}/free-liability`, { signal })).data as SafetyFreeLiabilityRow[];
  },

  /** POST /safety-system/units/{unitrecno}/free-liability `{week_of, amount}` — 201; 422 invalid, 404 no current driver. */
  async addFreeLiability(unitrecno: number, body: SafetyFreeLiabilityAdd): Promise<SafetyFreeLiabilityAddResponse> {
    return (await api.post(`/safety-system/units/${unitrecno}/free-liability`, body)).data as SafetyFreeLiabilityAddResponse;
  },

  /** GET /safety-system/units/{unitrecno}/notifications — e-mails logged against the unit and its current owner. */
  async unitNotifications(unitrecno: number, signal?: AbortSignal): Promise<SafetyUnitNotification[]> {
    return (await api.get(`/safety-system/units/${unitrecno}/notifications`, { signal })).data as SafetyUnitNotification[];
  },
};

export default safetySystemService;
